/**
 * CaseCardRenderer Component
 *
 * Renders a single case card with all its content sections in the multi-card
 * moderation queue view. Handles card focus, click, keyboard interactions,
 * and the "Labels Applied" overlay state.
 */

import {
  ModerationCaseCard,
  ModerationCaseCardHeader,
  ModerationCaseCardSection,
  ModerationTextContent,
  ModerationImageContent,
  ModerationAudioContent,
  ModerationLabelsContent,
  LabelsAppliedOverlay,
} from '@moderation/components/ModerationCaseCard';
import * as cardStyles from '@moderation/components/ModerationCaseCard/ModerationCaseCard.css';
import type { Content, ModerationCase } from '@moderation/domain/ModerationCase';
import type { CombinedLabel } from '@moderation/domain/ModerationLabels';
import type { CaseCardState } from '@moderation/domain/ModerationSession';
import type { PreviewOptions } from '@moderation/domain/PreviewOptions';
import { extractUserComment, extractAllMetadata, getTextIcon, getImageIcon, getAudioIcon, getUsernameIcon } from '@moderation/utils';
import { parseLabelSeverityDisplay } from '@moderation/utils/labelSeverityOptions';
import { getTextEffectClasses, getImageEffectClasses } from '@moderation/utils/previewEffectClasses';
import { Badge, BadgeSize, Icon, IconSize, Label, Text } from '@soteria/design-system-ve';
import { type MouseEvent, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import * as styles from '../ModerationWorkQueuePage.css';

/**
 * Renders a single content item (text, image, or audio) as a card section.
 * Extracted to reduce cyclomatic complexity of CaseCardRenderer.
 */
function renderContentSection(content: Content, index: number, previewOptions?: PreviewOptions): ReactNode {
  if (content.contentType === 'text') {
    const textMsg = {
      id: content.contentId,
      label: content.humanModerationViewHeader || 'Message',
      content: content.data,
      contentId: content.contentId,
    };

    return (
      <ModerationCaseCardSection key={content.contentId} hideHeader count={1} icon={getTextIcon()} title="Text">
        <ModerationTextContent contentEffectClassName={getTextEffectClasses(previewOptions)} messages={[textMsg]} />
      </ModerationCaseCardSection>
    );
  }

  if (content.contentType === 'image') {
    const imgItem = {
      id: content.contentId,
      url: content.data,
      alt: content.humanModerationViewHeader || `Image ${index + 1}`,
      contentId: content.contentId,
      type: 'image' as const,
    };

    return (
      <ModerationCaseCardSection key={content.contentId} hideHeader count={1} icon={getImageIcon()} title="Image">
        <ModerationImageContent imageEffectClassName={getImageEffectClasses(previewOptions)} images={[imgItem]} />
      </ModerationCaseCardSection>
    );
  }

  if (content.contentType === 'audio') {
    const audioItem = {
      id: content.contentId,
      url: content.data,
      contentId: content.contentId,
      type: 'audio' as const,
      label: content.humanModerationViewHeader || undefined,
    };

    return (
      <ModerationCaseCardSection key={content.contentId} hideHeader count={1} icon={getAudioIcon()} title="Audio">
        <ModerationAudioContent audioItems={[audioItem]} />
      </ModerationCaseCardSection>
    );
  }

  if (content.contentType === 'username') {
    const usernameMsg = {
      id: content.contentId,
      label: content.humanModerationViewHeader || 'Username',
      content: content.data,
      contentId: content.contentId,
    };

    return (
      <ModerationCaseCardSection key={content.contentId} hideHeader count={1} icon={getUsernameIcon()} title="Username">
        <ModerationTextContent contentEffectClassName={getTextEffectClasses(previewOptions)} leadingIcon={getUsernameIcon()} messages={[usernameMsg]} />
      </ModerationCaseCardSection>
    );
  }

  return null;
}

/** Renders the user comment section */
function renderFeedbackComment(userComment: string | null) {
  return userComment ? (
    <Text color="secondary" size="sm">
      <strong>Comment:</strong> {userComment}
    </Text>
  ) : (
    <Text color="secondary" size="sm">
      No comment provided
    </Text>
  );
}

/** Renders the reporter feedback labels section */
function renderFeedbackLabels(feedbackLabels: string[]) {
  if (feedbackLabels.length === 0) {
    return (
      <Text color="secondary" size="sm">
        No feedback labels
      </Text>
    );
  }

  return (
    <div className={cardStyles.labelsContainer}>
      <div className={cardStyles.labelsWrapper}>
        <div className={cardStyles.labelsHeaderGroup}>
          <Icon icon={Label} iconSize={IconSize.XSmall} />
          <span className={cardStyles.labelsHeaderText}>Feedback Labels</span>
        </div>
        {feedbackLabels.map((label: string) => (
          <Badge key={label} size={BadgeSize.Small} text={label.replace(/_/g, ' ')} />
        ))}
      </div>
    </div>
  );
}

/** Props for the individual case card renderer */
export interface CaseCardRendererProps {
  moderationCase: ModerationCase;
  isFocused: boolean;
  onFocus: (caseId: string) => void;
  /** Per-card session state (status, applied labels, etc.) */
  cardState?: CaseCardState;
  /** Callback to undo applied labels on a card */
  onUndo: (caseId: string) => void;
  /** Combined label list for resolving encoded `"labelId:severity"` display names */
  labels: CombinedLabel[];
  /** Whether to show the action menu trigger ("..." button) — typically only for the focused card */
  showActionMenu?: boolean;
  /** DOM ID for the action menu trigger button (accessibility) */
  actionMenuTriggerId?: string;
  /** Callback when the action menu trigger is toggled */
  onActionMenuToggle?: () => void;
  /** Preview effect toggle states for moderator well-being controls */
  previewOptions?: PreviewOptions;
}

/**
 * Manages per-card blur reveal state, resetting when page-level blur is toggled OFF.
 */
function usePerCardBlur(previewOptions?: PreviewOptions) {
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    if (!previewOptions?.blur) setIsRevealed(false);
  }, [previewOptions?.blur]);

  const handleBlurToggle = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    setIsRevealed(prev => !prev);
  }, []);

  const effectivePreviewOptions = !previewOptions ? undefined : previewOptions.blur && !isRevealed ? previewOptions : { ...previewOptions, blur: false };

  return { isRevealed, handleBlurToggle, effectivePreviewOptions };
}

/**
 * Extracts and normalizes content labels from the first content item in a moderation case.
 */
function extractContentLabels(moderationCase: ModerationCase) {
  const firstContent = moderationCase?.report?.contents?.[0];
  const allLabels =
    firstContent?.labels?.flatMap(labelGroup =>
      labelGroup.labels.map(l => ({
        name: l.name,
        confidence: l.confidence,
        severity: 'MEDIUM',
      }))
    ) || [];

  return allLabels.map((label, index) => ({
    ...label,
    id: `${label.name}-${label.confidence}-${index}`,
    severity: label.severity.toLowerCase() as 'low' | 'medium' | 'high' | 'critical',
  }));
}

const ACTIVATION_KEYS = new Set(['Enter', ' ']);

/**
 * Extracts and memoizes all data needed to render a case card from a moderation case and card state.
 */
function useCaseCardData(moderationCase: ModerationCase, cardState: CaseCardState | undefined, labels: CombinedLabel[]) {
  const isLabelsApplied = cardState?.status === 'labels-applied';
  const appliedLabelNames = useMemo(
    () => (cardState?.appliedLabels ?? []).map(encoded => parseLabelSeverityDisplay(encoded, labels)),
    [cardState?.appliedLabels, labels]
  );

  const userComment = extractUserComment(moderationCase);
  const feedbackLabels = moderationCase.report?.feedbackLabels ?? [];
  const metadata = extractAllMetadata(moderationCase);
  const labelsWithId = useMemo(() => extractContentLabels(moderationCase), [moderationCase]);
  const contents = moderationCase.report?.contents ?? [];

  return { isLabelsApplied, appliedLabelNames, userComment, feedbackLabels, metadata, labelsWithId, contents };
}

/**
 * Renders a single case card with all its content sections.
 * Extracted from ModerationWorkQueuePage to follow one-component-per-file rule.
 */
export function CaseCardRenderer({
  moderationCase,
  isFocused,
  onFocus,
  cardState,
  onUndo,
  labels,
  showActionMenu,
  actionMenuTriggerId,
  onActionMenuToggle,
  previewOptions,
}: CaseCardRendererProps) {
  const { isRevealed, handleBlurToggle, effectivePreviewOptions } = usePerCardBlur(previewOptions);
  const { isLabelsApplied, appliedLabelNames, userComment, feedbackLabels, metadata, labelsWithId, contents } = useCaseCardData(
    moderationCase,
    cardState,
    labels
  );
  const showBlurToggle = Boolean(previewOptions?.blur);

  const handleCardClick = useCallback(() => {
    onFocus(moderationCase.caseId);
  }, [onFocus, moderationCase.caseId]);

  const handleCardKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (ACTIVATION_KEYS.has(event.key)) {
        event.preventDefault();
        onFocus(moderationCase.caseId);
      }
    },
    [onFocus, moderationCase.caseId]
  );

  return (
    <div
      aria-selected={isFocused}
      className={styles.cardItem}
      role="option"
      tabIndex={isFocused ? 0 : -1}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
    >
      <ModerationCaseCard isFocused={isFocused}>
        {/* Labels Applied Overlay */}
        {isLabelsApplied && <LabelsAppliedOverlay appliedLabels={appliedLabelNames} onUndo={() => onUndo(moderationCase.caseId)} />}

        <ModerationCaseCardHeader
          showCheckbox
          actionMenuTriggerId={actionMenuTriggerId}
          fullTimestamp={metadata.fullTimestamp}
          isBlurred={!isRevealed}
          isChecked={isFocused}
          segmentName={metadata.segmentName}
          showActionMenu={showActionMenu}
          showBlurToggle={showBlurToggle}
          timestamp={metadata.formattedTimestamp}
          userId={metadata.subjectId}
          username={metadata.username}
          onActionMenuToggle={onActionMenuToggle}
          onBlurToggle={handleBlurToggle}
        />

        {/* Content sections - rendered in source array order */}
        {contents.map((content: Content, index: number) => renderContentSection(content, index, effectivePreviewOptions))}

        {/* Feedback Comment — plain text with bold "Comment:" header */}
        <div className={cardStyles.sectionSeparator}>{renderFeedbackComment(userComment)}</div>

        {/* Feedback labels from the reporter */}
        <div className={cardStyles.sectionSeparator}>{renderFeedbackLabels(feedbackLabels)}</div>

        {labelsWithId.length > 0 && <ModerationLabelsContent labels={labelsWithId} />}
      </ModerationCaseCard>
    </div>
  );
}
